// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/** A chat message as it appears in a training row. */
export interface Message {
  role?: unknown;
  content?: unknown;
  name?: unknown;
  /** Present on assistant rows that call tools. Counted as serialised JSON. */
  tool_calls?: unknown;
  weight?: unknown;
}

/**
 * Why a row could not be counted.
 *
 * Kept as a closed set rather than a free-text string because the interface has
 * to say how many rows of each kind there are, and a message that varies by row
 * cannot be grouped.
 */
export type RowProblem =
  | 'not-json'
  | 'no-messages'
  | 'empty-messages'
  | 'message-not-object'
  | 'content-not-string'
  | 'no-assistant';

/** One counted record. Held only in aggregate — never a million of these. */
export interface RowCount {
  /** 1-based ordinal in the file, counting every non-blank line. */
  readonly ordinal: number;
  /** Byte offset of the line's first byte, so the row can be re-read for export. */
  readonly offset: number;
  readonly bytes: number;
  /** Tokens the model actually sees: message content + framing. */
  readonly tokens: number;
  /** Tokens in message content alone, without framing. */
  readonly contentTokens: number;
  /** Per-role content tokens, indexed by ROLES. */
  readonly byRole: Int32Array;
  readonly messages: number;
  readonly problem: RowProblem | null;
}

/** The roles counted separately. Anything else lands in `other`. */
export const ROLES = ['system', 'user', 'assistant', 'tool', 'other'] as const;
export type Role = (typeof ROLES)[number];

export interface Histogram {
  /** Upper bound of each bucket, in tokens. */
  readonly edges: readonly number[];
  readonly counts: Int32Array;
}

/**
 * Everything the counter produces. This is what crosses back from the workers
 * and what every number on screen is derived from.
 */
export interface Totals {
  rows: number;
  blankLines: number;
  bytes: number;
  /** Sum of per-row `tokens`, uncapped. NOT the billing figure. */
  tokens: number;
  /**
   * Sum of `min(row tokens, limit)` — the billing figure, before epochs.
   *
   * Accumulated during the single pass rather than computed from a list of
   * per-row counts, because a two-million-row file has two million counts and
   * keeping them would defeat the streaming design. The consequence is that
   * changing the LIMIT requires another pass, while changing the EPOCHS is a
   * multiplication.
   */
  cappedTokens: number;
  contentTokens: number;
  /** Sum of per-role content tokens. */
  byRole: Int32Array;
  messages: number;
  /** Rows whose token count exceeds the limit currently selected. */
  overLimit: number;
  /**
   * Tokens that fall past the limit on over-limit rows and are therefore
   * DELETED by the provider before training. The headline loss figure.
   */
  truncatedTokens: number;
  longest: number;
  shortest: number;
  problems: Record<RowProblem, number>;
  histogram: Histogram;
}

export interface Progress {
  bytesRead: number;
  bytesTotal: number;
  rows: number;
  /** Decompressed bytes per second, measured rather than assumed. */
  bytesPerSecond: number;
}

/** A row that broke the limit, kept for the work-queue export. */
export interface OverLimitRow {
  readonly ordinal: number;
  readonly offset: number;
  readonly length: number;
  readonly tokens: number;
  readonly truncated: number;
  /** Tokens of the final assistant turn that fall past the limit, if known. */
  readonly assistantLost: number;
  /**
   * The row's own text, so `over-limit.jsonl` can be written without re-reading
   * the file.
   *
   * This is not an optimisation, it is the only thing that works. The offset
   * above is into the DECOMPRESSED stream, and a `.jsonl.gz` cannot be seeked to
   * a decompressed offset — rebuilding the export from offsets would silently
   * produce garbage for every gzipped input, which is most of them. Text is kept
   * under a byte budget (`OVER_LIMIT_BYTES`); rows past it keep their metadata
   * and are counted in `truncatedByCap`.
   */
  readonly text: string | null;
}
